/**
 * MANAGEMENT (14) · Subscriptions WRITE layer — the authoritative plan change + cancel the super-admin
 * console issues against a single lab's central entitlement projection. ADDITIVE to the shell data layer:
 * it reuses the shell's `managementKeys` factory, the shared `api` verb-client, `useApiMutation`, and the
 * already-declared `SubscriptionRow` read shape (./../types) — it re-declares NONE of them. Kept in this
 * NEW co-located file (not the shell `../api.ts`) so the shell stays untouched while a sibling agent works.
 *
 * Server contract — code-verified against server/src/modules/management/subscriptions.{routes,validators,
 * controller}.js:
 *   PUT    /management/subscriptions/:laboratoireId   management.subscription.manage   (change/reissue)
 *   DELETE /management/subscriptions/:laboratoireId   management.subscription.manage   (cancel/soft-delete)
 *
 * Both write the SINGLE current AbonnementProjection row by reissue/supersede (L-1, correct-by-reissue —
 * never write-once): the PUT bumps `version` + un-deletes a previously cancelled row; the DELETE is the
 * SOFT-delete (statut → ANNULEE, cloud SKUs stripped, the lab's own LOCAL bundles left intact, L-4). The
 * wire body is snake_case (the validator's schema); the projection the on-box entitlement gate consumes is
 * refreshed server-side via `invalidateEntitlement`. Both responses return the `{ laboratoire, subscription }`
 * row, and both invalidate the whole cross-lab subscriptions namespace so every filtered list re-reads.
 */
import { api, managementKeys, useApiMutation } from "../api";
import type { SubscriptionRow, SubscriptionStatut } from "../types";

/**
 * PUT /management/subscriptions/:laboratoireId body — the FULL desired projection state (the operator
 * declares it; there is no separate create/update verb on the wire). `statut` is mandatory; everything else
 * is optional. snake_case mirrors the server Zod schema (`changePlanSchema`) exactly so the contract can
 * never drift. Module SKU lists are free strings (the central catalogue owns the canonical SKU vocabulary).
 */
export interface ChangePlanVars {
  /** Marketing/plan label, e.g. "PRO" | "STARTER". Nullable — a comp/manual grant may carry no plan. */
  plan?: string | null;
  /** The authoritative subscription statut — REQUIRED (server rejects an unknown/absent statut). */
  statut: SubscriptionStatut;
  /** The flat entitled module set (cloud ∪ local) — optional; the server stores [] when omitted. */
  modules?: string[];
  /** The cloud SKUs (gated when the statut is not active-enough). */
  modules_cloud?: string[];
  /** The on-box local bundles (never held hostage; survive a cancel). */
  modules_local?: string[];
  /** WHY the change — recorded on the projection (e.g. comp_granted | upgraded | reactivated). */
  reason?: string | null;
  /** An external reference (a billing/contract id) the central authority may attach. */
  source_ref?: string | null;
}

/** DELETE /management/subscriptions/:laboratoireId body — cancel (soft-delete). Optional motif only. */
export interface CancelSubscriptionVars {
  reason?: string | null;
}

/**
 * PUT /management/subscriptions/:laboratoireId — CHANGE (reissue/supersede) a lab's plan. Invalidates the
 * whole `["platform","management","subscriptions",…]` namespace so every filtered list + the per-lab
 * drill-down re-reads the fresh projection. Returns the updated `{ laboratoire, subscription }` row.
 */
export function useChangePlan(laboratoireId: string) {
  return useApiMutation<SubscriptionRow, ChangePlanVars>(
    (vars) => api.put<SubscriptionRow>(`/management/subscriptions/${laboratoireId}`, vars),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * DELETE /management/subscriptions/:laboratoireId — CANCEL (soft-delete → ANNULEE). The lab's LOCAL bundles
 * are intentionally KEPT; only the cloud SKUs drop. Never a hard delete (the medico-legal/billing trail
 * survives; a later change reissues). Same namespace invalidation as the change.
 */
export function useCancelSubscription(laboratoireId: string) {
  return useApiMutation<SubscriptionRow, CancelSubscriptionVars>(
    (vars) => api.delete<SubscriptionRow>(`/management/subscriptions/${laboratoireId}`, vars),
    { invalidate: [managementKeys.root] },
  );
}
